import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useMsal } from "@azure/msal-react";
import { mergeStyleSets } from "@fluentui/react";
import { CheckCircle, Clock, ChartBar, Robot, Database, MagnifyingGlass, Wrench, ArrowRight, ArrowLeft, Warning } from "@phosphor-icons/react";

/* ── Palette ── */
const GREEN = "#2e873d";
const p = {
  bg: "#FAFAFA",
  surface: "#FFFFFF",
  border: "#E4E4E4",
  text: "#232323",
  text2: "#484848",
  text3: "#747474",
  green: GREEN,
  greenLight: "#F0F7F1",
  greenBorder: "#C3DFC7",
};

/* ── Styles ── */
const s = mergeStyleSets({
  page: { minHeight: "100vh", background: p.bg, fontFamily: "'Segoe UI', system-ui, sans-serif" },
  header: {
    position: "sticky" as const, top: 0, zIndex: 50,
    background: p.surface, borderBottom: `1px solid ${p.border}`,
  },
  headerInner: {
    maxWidth: 900, margin: "0 auto", padding: "0 28px", height: 56,
    display: "flex", alignItems: "center", gap: 12,
  },
  logo: {
    width: 28, height: 28, borderRadius: 6,
    background: GREEN, display: "flex", alignItems: "center", justifyContent: "center",
  },
  logoText: { color: "#fff", fontWeight: 900, fontSize: 13, letterSpacing: "-0.02em" },
  headerTitle: { fontSize: 14, fontWeight: 700, color: p.text },
  headerSub: { fontSize: 12, color: p.text3 },

  wrap: { maxWidth: 680, margin: "0 auto", padding: "48px 28px 80px" },

  /* Progress */
  progress: { display: "flex", alignItems: "center", gap: 0, marginBottom: 40 },
  stepItem: { display: "flex", alignItems: "center", gap: 0, flex: 1 },
  stepDot: {
    width: 30, height: 30, borderRadius: "50%",
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: 12, fontWeight: 700, flexShrink: 0,
  },
  stepLabel: { fontSize: 11, fontWeight: 600, marginLeft: 8, whiteSpace: "nowrap" as const, letterSpacing: "0.04em" },
  stepLine: { flex: 1, height: 2, margin: "0 10px" },

  /* Cards */
  card: {
    background: p.surface, border: `1px solid ${p.border}`,
    borderRadius: 10, padding: "28px 28px 24px", marginBottom: 16,
    boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
  },
  cardTitle: { fontSize: 16, fontWeight: 700, color: p.text, marginBottom: 4 },
  cardSub: { fontSize: 13, color: p.text2, lineHeight: "1.5", marginBottom: 20 },

  /* Email field */
  emailWrap: { position: "relative" as const, marginBottom: 12 },
  emailInput: {
    width: "100%", padding: "11px 14px", fontSize: 14, borderRadius: 6,
    border: `1px solid ${p.border}`, outline: "none", boxSizing: "border-box" as const,
    fontFamily: "inherit", color: p.text, background: p.surface,
    transition: "border-color .15s",
    selectors: { "&:focus": { borderColor: GREEN } },
  },
  emailNote: { fontSize: 12, color: p.text3, marginTop: 6 },

  /* Capability groups */
  capGroup: { marginBottom: 20 },
  capGroupHead: {
    display: "flex", alignItems: "center", gap: 8,
    marginBottom: 10, paddingBottom: 8, borderBottom: `1px solid ${p.border}`,
  },
  capGroupIcon: { color: GREEN, flexShrink: 0 },
  capGroupTitle: { fontSize: 13, fontWeight: 700, color: p.text, letterSpacing: "0.04em", textTransform: "uppercase" as const },
  capGroupSub: { fontSize: 12, color: p.text3, marginLeft: "auto" },

  capPublicPill: {
    display: "inline-flex", alignItems: "center", gap: 4,
    padding: "4px 10px", background: p.greenLight, border: `1px solid ${p.greenBorder}`,
    borderRadius: 12, fontSize: 11.5, color: GREEN, fontWeight: 600,
    marginRight: 8, marginBottom: 6,
  },
  capItem: {
    display: "flex", alignItems: "flex-start", gap: 12,
    padding: "12px 14px", borderRadius: 7, border: `1px solid ${p.border}`,
    marginBottom: 8, cursor: "pointer", transition: "all .12s", background: p.surface,
    selectors: { "&:hover": { borderColor: GREEN, background: p.greenLight } },
  },
  capItemSelected: {
    borderColor: GREEN, background: p.greenLight,
  },
  capCheckbox: {
    width: 18, height: 18, borderRadius: 4, border: `1.5px solid ${p.border}`,
    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1,
  },
  capCheckboxSelected: { background: GREEN, borderColor: GREEN },
  capItemContent: { flex: 1 },
  capItemLabel: { fontSize: 13.5, fontWeight: 600, color: p.text, marginBottom: 2 },
  capItemDesc: { fontSize: 12, color: p.text3, lineHeight: "1.45" },

  /* Notes */
  notesInput: {
    width: "100%", padding: "10px 14px", fontSize: 13, borderRadius: 6,
    border: `1px solid ${p.border}`, outline: "none", resize: "vertical" as const,
    fontFamily: "inherit", color: p.text, minHeight: 72, boxSizing: "border-box" as const,
    selectors: { "&:focus": { borderColor: GREEN } },
  },

  /* Buttons */
  btnRow: { display: "flex", alignItems: "center", gap: 10, marginTop: 24, justifyContent: "space-between" },
  btnPrimary: {
    display: "inline-flex", alignItems: "center", gap: 8,
    padding: "10px 22px", background: GREEN, color: "#fff",
    border: "none", borderRadius: 7, fontSize: 14, fontWeight: 600,
    cursor: "pointer", fontFamily: "inherit",
    selectors: { "&:hover": { background: "#25692f" }, "&:disabled": { opacity: .5, cursor: "not-allowed" } },
  },
  btnSecondary: {
    display: "inline-flex", alignItems: "center", gap: 6,
    padding: "10px 18px", background: "none", color: p.text2,
    border: `1px solid ${p.border}`, borderRadius: 7, fontSize: 14, fontWeight: 500,
    cursor: "pointer", fontFamily: "inherit",
    selectors: { "&:hover": { background: p.border } },
  },

  /* Error */
  error: {
    padding: "10px 14px", borderRadius: 6, background: "#FEF2F2",
    border: "1px solid #FECACA", color: "#B91C1C", fontSize: 13, marginBottom: 16,
    display: "flex", alignItems: "flex-start", gap: 8,
  },

  /* Success */
  successCard: {
    background: p.surface, border: `1px solid ${p.greenBorder}`,
    borderTop: `4px solid ${GREEN}`, borderRadius: 10,
    padding: "40px 32px", textAlign: "center" as const,
    boxShadow: "0 2px 8px rgba(46,135,61,0.08)",
  },
  successIcon: { color: GREEN, marginBottom: 16, display: "block" },
  successTitle: { fontSize: 22, fontWeight: 800, color: p.text, marginBottom: 8, letterSpacing: "-0.02em" },
  successSub: { fontSize: 14, color: p.text2, lineHeight: "1.6", maxWidth: 420, margin: "0 auto 20px" },
  successNote: {
    fontSize: 12, color: p.text3, display: "flex", alignItems: "center",
    justifyContent: "center", gap: 6, marginTop: 16,
  },
});

/* ── Capability definitions (static, aligned with tab_catalog) ── */
interface CapOption { id: string; label: string; desc: string; group: string; tab: string; items: string[] }

const GOVERNED_CAPS: CapOption[] = [
  {
    id: "finance", group: "Dashboards & Data", tab: "dashboards",
    label: "Finance & Contract Expenditure",
    desc: "GL actuals, budget vs actual, cost centre hierarchy, project spend, and purchase order commitments.",
    items: ["finance_contract_expenditure"],
  },
  {
    id: "assets", group: "Dashboards & Data", tab: "dashboards",
    label: "Assets & Infrastructure",
    desc: "Asset condition, lifecycle, service requests and maintenance data.",
    items: ["assets_infrastructure"],
  },
  {
    id: "crm", group: "Dashboards & Data", tab: "dashboards",
    label: "CRM Request Analytics",
    desc: "Oracle Service Cloud request volumes, SLA compliance, channel breakdown and department performance.",
    items: ["crm_requests"],
  },
  {
    id: "customer_survey", group: "Dashboards & Data", tab: "dashboards",
    label: "Customer Survey Analytics",
    desc: "Post-Resolution Survey satisfaction, ease of service, resolution rates, trends and verbatim feedback.",
    items: ["customer_survey"],
  },
  {
    id: "chat_finance", group: "AI Chat Agents", tab: "chat",
    label: "Finance Chat Agent",
    desc: "Ask natural-language questions against Oracle Fusion GL, budget, and project data.",
    items: ["finance"],
  },
  {
    id: "chat_assets", group: "AI Chat Agents", tab: "chat",
    label: "Assets Chat Agent",
    desc: "Query Conquest asset records, condition assessments, and maintenance history.",
    items: ["assets"],
  },
  {
    id: "chat_property", group: "AI Chat Agents", tab: "chat",
    label: "Property & Rates Chat Agent",
    desc: "Query TechOne property, rates, permits, and infringement data.",
    items: ["property"],
  },
  {
    id: "data_finance", group: "Datasets", tab: "datasets",
    label: "Finance datasets",
    desc: "Full access to Finance GL and budget datasets in the portal dataset catalogue.",
    items: ["finance"],
  },
  {
    id: "data_assets", group: "Datasets", tab: "datasets",
    label: "Assets datasets",
    desc: "Conquest assets dataset access.",
    items: ["assets"],
  },
  {
    id: "data_property", group: "Datasets", tab: "datasets",
    label: "Property / Rates / Permits / Infringements datasets",
    desc: "TechOne property domain datasets.",
    items: ["property", "rates", "permits", "infringements"],
  },
  {
    id: "search_finance", group: "Search", tab: "search",
    label: "Finance search (Cost Centres, Projects, Suppliers, Purchase Orders)",
    desc: "Search and look up finance entities across the council's Oracle Fusion data.",
    items: ["cost_centre", "project", "supplier", "purchase_order"],
  },
  {
    id: "search_property", group: "Search", tab: "search",
    label: "Property / Asset / Service Request search",
    desc: "Search properties, assets, permits, infringements, and service requests.",
    items: ["property", "permit", "infringement", "asset", "service_request"],
  },
  {
    id: "builder", group: "Build Your Dashboard", tab: "build_dashboard",
    label: "Dashboard Builder",
    desc: "Use the AI-powered dashboard builder to create custom views from any dataset you have access to.",
    items: ["finance", "assets", "property"],
  },
];

const GROUP_ORDER = ["Dashboards & Data", "AI Chat Agents", "Datasets", "Search", "Build Your Dashboard"];
const GROUP_ICONS: Record<string, React.ReactNode> = {
  "Dashboards & Data": <ChartBar size={15} weight="bold" />,
  "AI Chat Agents":    <Robot size={15} weight="bold" />,
  "Datasets":          <Database size={15} weight="bold" />,
  "Search":            <MagnifyingGlass size={15} weight="bold" />,
  "Build Your Dashboard": <Wrench size={15} weight="bold" />,
};

/* ── Component ── */
export default function Onboard() {
  const { accounts } = useMsal();
  const msalAccount = accounts[0];

  const [step, setStep] = useState(1); // 1 = email, 2 = capabilities, 3 = done
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [pendingStatus, setPendingStatus] = useState<"pending" | "denied" | null>(null);
  const [denialNote, setDenialNote] = useState("");
  const [isAdditionalRequest, setIsAdditionalRequest] = useState(false);
  const [currentGrants, setCurrentGrants] = useState<Array<{tab: string; item: string}>>([]);
  const [additionalRequestStatus, setAdditionalRequestStatus] = useState<"pending" | "denied" | null>(null);

  const ALLOWED_DOMAIN = (process.env.REACT_APP_ALLOWED_EMAIL_DOMAIN || "boroondara.vic.gov.au").toLowerCase();

  /* Pre-fill email from MSAL session */
  useEffect(() => {
    if (msalAccount?.username) {
      setEmail(msalAccount.username.toLowerCase());
    }
  }, [msalAccount]);

  /* Check if user already has a pending/denied request */
  useEffect(() => {
    const upn = (msalAccount?.username || email).toLowerCase();
    if (!upn) return;
    axios.get(`/api/onboard/status?email=${encodeURIComponent(upn)}`)
      .then(r => {
        if (r.data.status === "pending") {
          setPendingStatus("pending");
        } else if (r.data.status === "denied") {
          setPendingStatus("denied");
          if (r.data.reviewNotes) setDenialNote(r.data.reviewNotes);
          // Pre-fill previous selections so the user can adjust and re-apply
          if (r.data.requestedCapabilities?.governed) {
            const prev = r.data.requestedCapabilities.governed as Record<string, string[]>;
            const prevIds = new Set<string>();
            for (const cap of GOVERNED_CAPS) {
              const tabItems: string[] = prev[cap.tab] || [];
              if (cap.items.every(item => tabItems.includes(item))) prevIds.add(cap.id);
            }
            setSelected(prevIds);
          }
        } else if (r.data.status === "active") {
          // Active user — show "add more access" flow instead of redirecting
          setIsAdditionalRequest(true);
          setCurrentGrants(r.data.grants || []);
          if (r.data.additionalRequestStatus === "pending") {
            setAdditionalRequestStatus("pending");
          } else if (r.data.additionalRequestStatus === "denied") {
            setAdditionalRequestStatus("denied");
            if (r.data.reviewNotes) setDenialNote(r.data.reviewNotes);
            // Pre-fill previous selections
            if (r.data.requestedCapabilities?.governed) {
              const prev = r.data.requestedCapabilities.governed as Record<string, string[]>;
              const prevIds = new Set<string>();
              for (const cap of GOVERNED_CAPS) {
                const tabItems: string[] = prev[cap.tab] || [];
                if (cap.items.every(item => tabItems.includes(item))) prevIds.add(cap.id);
              }
              setSelected(prevIds);
            }
          }
          // Jump straight to the capability picker (email is known from MSAL)
          setStep(2);
        }
      })
      .catch(() => {/* ignore */});
  }, [msalAccount, email]);

  const validateEmail = useCallback(() => {
    const v = email.trim().toLowerCase();
    if (!v) { setEmailError("Email address is required"); return false; }
    if (!v.includes("@")) { setEmailError("Enter a valid email address"); return false; }
    if (!v.endsWith(`@${ALLOWED_DOMAIN}`)) {
      setEmailError(`Only @${ALLOWED_DOMAIN} email addresses can request access`);
      return false;
    }
    setEmailError("");
    return true;
  }, [email, ALLOWED_DOMAIN]);

  // Returns true if ALL items for this cap are already in the user's current grants
  const isCapGranted = (cap: CapOption): boolean =>
    isAdditionalRequest && cap.items.every(item =>
      currentGrants.some(g => g.tab === cap.tab && g.item === item)
    );

  const toggleCap = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError("");
    try {
      // Build capabilities payload
      const chosenCaps = GOVERNED_CAPS.filter(c => selected.has(c.id));
      const governed: Record<string, string[]> = {};
      chosenCaps.forEach(c => {
        if (!governed[c.tab]) governed[c.tab] = [];
        c.items.forEach(item => {
          if (!governed[c.tab].includes(item)) governed[c.tab].push(item);
        });
      });

      await axios.post("/api/onboard", {
        email: email.trim().toLowerCase(),
        displayName: msalAccount?.name || email.split("@")[0],
        requestedCapabilities: { governed },
        notes: notes.trim() || undefined,
      });
      setStep(3);
    } catch (err: any) {
      const msg = err?.response?.data?.error || "Failed to submit request. Please try again.";
      const code = err?.response?.data?.code;
      if (code === "REQUEST_PENDING") {
        setStep(3);
        setPendingStatus("pending");
        return;
      }
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  /* ── Render helpers ── */
  const StepIndicator = () => {
    const steps = ["Your details", "Select access", "Submitted"];
    return (
      <div className={s.progress}>
        {steps.map((label, i) => {
          const n = i + 1;
          const done = step > n;
          const active = step === n;
          return (
            <div key={n} className={s.stepItem} style={{ flex: i < steps.length - 1 ? 1 : 0 }}>
              <div className={s.stepDot} style={{
                background: done ? GREEN : active ? GREEN : p.border,
                color: done || active ? "#fff" : p.text3,
              }}>
                {done ? <CheckCircle size={14} weight="bold" /> : n}
              </div>
              <span className={s.stepLabel} style={{ color: active ? GREEN : done ? p.text2 : p.text3 }}>{label}</span>
              {i < steps.length - 1 && (
                <div className={s.stepLine} style={{ background: step > n ? GREEN : p.border }} />
              )}
            </div>
          );
        })}
      </div>
    );
  };

  /* ── Step 3: Success / Pending ── */
  if (step === 3 || pendingStatus) {
    const isDenied = pendingStatus === "denied";
    return (
      <div className={s.page}>
        <div className={s.wrap} style={{ paddingTop: 72 }}>
          <div className={s.successCard} style={{ borderTopColor: isDenied ? "#9CA3AF" : GREEN }}>
            {isDenied ? (
              <Warning size={48} weight="duotone" style={{ color: "#9CA3AF", display: "block", margin: "0 auto 16px" }} />
            ) : (
              <Clock size={48} weight="duotone" style={{ color: GREEN, display: "block", margin: "0 auto 16px" }} />
            )}
            <div className={s.successTitle}>
              {isDenied ? "Access request was not approved" : "Request submitted — awaiting approval"}
            </div>
            <div className={s.successSub}>
              {isDenied
                ? "Your access request could not be approved at this time."
                : "An admin has been notified and will review your request shortly. You'll receive an email at " + (email || msalAccount?.username || "your address") + " once a decision has been made."
              }
            </div>
            {isDenied && denialNote && (
              <div style={{
                background: "#FFFBEB", borderLeft: "3px solid #F59E0B",
                borderRadius: "0 6px 6px 0", padding: "10px 14px",
                margin: "16px auto 0", maxWidth: 380, textAlign: "left" as const,
                fontSize: 13, color: "#78350F", fontStyle: "italic",
              }}>
                "{denialNote}"
              </div>
            )}
            {isDenied && (
              <div style={{ marginTop: 24 }}>
                <button
                  className={s.btnPrimary}
                  style={{ margin: "0 auto", display: "inline-flex" }}
                  onClick={() => { setPendingStatus(null); setStep(2); }}
                >
                  Update and re-apply <ArrowRight size={15} />
                </button>
                <div style={{ fontSize: 12, color: p.text3, marginTop: 10 }}>
                  Your previous selections have been pre-filled — adjust them and re-submit.
                </div>
              </div>
            )}
            {!isDenied && (
              <div className={s.successNote}>
                <Clock size={13} /> Access requests are typically reviewed within one business day
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  /* ── Step 1: Email ── */
  if (step === 1) {
    return (
      <div className={s.page}>
        <header className={s.header}>
          <div className={s.headerInner}>
            <div className={s.logo}><span className={s.logoText}>B</span></div>
            <div>
              <div className={s.headerTitle}>Enterprise Data Portal</div>
              <div className={s.headerSub}>City of Boroondara</div>
            </div>
          </div>
        </header>

        <div className={s.wrap}>
          <StepIndicator />

          <div style={{ marginBottom: 28 }}>
            <h1 style={{ fontSize: 26, fontWeight: 800, color: p.text, margin: "0 0 8px", letterSpacing: "-0.02em" }}>
              Request portal access
            </h1>
            <p style={{ fontSize: 14, color: p.text2, margin: 0, lineHeight: "1.6" }}>
              The Boroondara Enterprise Data Portal provides access to council data, AI chat agents, and governed dashboards.
              Submit a request below and an administrator will review it within one business day.
            </p>
          </div>

          <div className={s.card}>
            <div className={s.cardTitle}>Your email address</div>
            <div className={s.cardSub}>
              Use your <strong>@{ALLOWED_DOMAIN}</strong> account.
              {msalAccount && " Your Microsoft account has been detected below."}
            </div>

            <div className={s.emailWrap}>
              <input
                className={s.emailInput}
                type="email"
                placeholder={`name@${ALLOWED_DOMAIN}`}
                value={email}
                readOnly={!!msalAccount?.username}
                onChange={e => { setEmail(e.target.value); setEmailError(""); }}
                onKeyDown={e => e.key === "Enter" && validateEmail() && setStep(2)}
                style={msalAccount?.username ? { background: "#F5F5F5", color: p.text2 } : undefined}
              />
            </div>
            {msalAccount?.username && (
              <div className={s.emailNote}>
                ✓ Signed in as <strong>{msalAccount.name || msalAccount.username}</strong> via your Boroondara Microsoft account.
              </div>
            )}
            {emailError && (
              <div className={s.error} style={{ marginTop: 10, marginBottom: 0 }}>
                <Warning size={16} style={{ flexShrink: 0, marginTop: 1 }} />
                {emailError}
              </div>
            )}
          </div>

          {/* What's included — public tier preview */}
          <div className={s.card} style={{ padding: "20px 24px" }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: p.text3, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 12 }}>
              Included with all approved accounts
            </div>
            <div style={{ display: "flex", flexWrap: "wrap" as const, gap: 6 }}>
              {[
                "Council Dashboard", "Economic Activity Dashboard",
                "ABS Census Chat Agent", "Geospatial Explorer",
                "ABS Census Dataset", "Economic Activity Dataset",
              ].map(label => (
                <span key={label} className={s.capPublicPill}>
                  <CheckCircle size={11} weight="bold" /> {label}
                </span>
              ))}
            </div>
          </div>

          {error && (
            <div className={s.error}>
              <Warning size={16} style={{ flexShrink: 0, marginTop: 1 }} />{error}
            </div>
          )}

          <div className={s.btnRow} style={{ justifyContent: "flex-end" }}>
            <button
              className={s.btnPrimary}
              onClick={() => validateEmail() && setStep(2)}
            >
              Select access <ArrowRight size={15} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* ── Step 2: Capability picker ── */
  const groupedCaps = GROUP_ORDER.map(group => ({
    group,
    caps: GOVERNED_CAPS.filter(c => c.group === group),
  })).filter(g => g.caps.length > 0);

  return (
    <div className={s.page}>
      <header className={s.header}>
        <div className={s.headerInner}>
          <div className={s.logo}><span className={s.logoText}>B</span></div>
          <div>
            <div className={s.headerTitle}>Enterprise Data Portal</div>
            <div className={s.headerSub}>City of Boroondara</div>
          </div>
        </div>
      </header>

      <div className={s.wrap}>
        <StepIndicator />

        <div style={{ marginBottom: 24 }}>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: p.text, margin: "0 0 6px", letterSpacing: "-0.02em" }}>
            {isAdditionalRequest ? "Request additional access" : "Select the access you need"}
          </h1>
          <p style={{ fontSize: 13, color: p.text2, margin: 0, lineHeight: "1.5" }}>
            {isAdditionalRequest
              ? "You already have portal access. Select any additional capabilities below to request."
              : "Choose which governed capabilities to request. Public content is included automatically. An admin will review your selections before granting access."}
          </p>
        </div>

        {/* Additional-request status banners */}
        {additionalRequestStatus === "pending" && (
          <div className={s.error} style={{ background: "#FFFBEB", borderColor: "#FDE68A", color: "#92400E", marginBottom: 16 }}>
            <Clock size={16} style={{ flexShrink: 0, marginTop: 1 }} />
            You already have a pending request for additional access. An admin will review it shortly.
          </div>
        )}
        {additionalRequestStatus === "denied" && (
          <div style={{ background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 6, padding: "12px 14px", marginBottom: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#92400E", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>Previous request was not approved</div>
            {denialNote && <div style={{ fontSize: 13, color: "#78350F", fontStyle: "italic", marginBottom: 8 }}>"{denialNote}"</div>}
            <div style={{ fontSize: 12, color: "#92400E" }}>Update your selections below and re-submit.</div>
          </div>
        )}

        {groupedCaps.map(({ group, caps }) => (
          <div key={group} className={s.card}>
            <div className={s.capGroupHead}>
              <span className={s.capGroupIcon}>{GROUP_ICONS[group]}</span>
              <span className={s.capGroupTitle}>{group}</span>
              <span className={s.capGroupSub}>{caps.filter(c => selected.has(c.id)).length}/{caps.length} selected</span>
            </div>
            {caps.map(cap => {
              const sel = selected.has(cap.id);
              const granted = isCapGranted(cap);
              return (
                <div
                  key={cap.id}
                  className={`${s.capItem} ${sel && !granted ? s.capItemSelected : ""}`}
                  onClick={() => !granted && toggleCap(cap.id)}
                  role="checkbox"
                  aria-checked={granted || sel}
                  aria-disabled={granted}
                  tabIndex={granted ? -1 : 0}
                  onKeyDown={e => !granted && e.key === " " && toggleCap(cap.id)}
                  style={granted ? { opacity: 0.65, cursor: "default", background: p.greenLight, borderColor: p.greenBorder } : undefined}
                >
                  <div className={`${s.capCheckbox} ${granted || sel ? s.capCheckboxSelected : ""}`}
                    style={granted ? { background: "#9CA3AF", borderColor: "#9CA3AF" } : undefined}>
                    {(granted || sel) && <CheckCircle size={12} weight="bold" color="#fff" />}
                  </div>
                  <div className={s.capItemContent}>
                    <div className={s.capItemLabel}>
                      {cap.label}
                      {granted && <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 600, color: "#6B7280", background: "#E5E7EB", borderRadius: 4, padding: "1px 6px" }}>Already granted</span>}
                    </div>
                    <div className={s.capItemDesc}>{cap.desc}</div>
                  </div>
                </div>
              );
            })}
          </div>
        ))}

        {/* Notes */}
        <div className={s.card} style={{ padding: "20px 24px" }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: p.text, marginBottom: 6 }}>
            Additional context <span style={{ color: p.text3, fontWeight: 400 }}>(optional)</span>
          </div>
          <textarea
            className={s.notesInput}
            placeholder="e.g. I need Finance access for the annual budget review process..."
            value={notes}
            onChange={e => setNotes(e.target.value)}
            rows={3}
          />
        </div>

        {error && (
          <div className={s.error}>
            <Warning size={16} style={{ flexShrink: 0, marginTop: 1 }} />{error}
          </div>
        )}

        <div className={s.btnRow}>
          <button className={s.btnSecondary} onClick={() => setStep(1)}>
            <ArrowLeft size={14} /> Back
          </button>
          <button
            className={s.btnPrimary}
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? "Submitting…" : (
              <>{isAdditionalRequest ? "Request additional access" : "Submit request"} <ArrowRight size={15} /></>
            )}
          </button>
        </div>

        <p style={{ fontSize: 12, color: p.text3, textAlign: "center" as const, marginTop: 24 }}>
          {isAdditionalRequest
            ? <>Requesting additional access for <strong>{email}</strong> · Your existing access will not be affected</>
            : <>Requesting access for <strong>{email}</strong> · You'll be notified by email once reviewed</>
          }
        </p>
      </div>
    </div>
  );
}
